import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Select, App, Space, Segmented } from 'antd';
import {
  FilterOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { getSalesLeaderboard, getAreaLeaderboard } from '../../../services/leaderboardService';
import { getPropertyAreasForRole } from '../../../services/dashboardService';
import {
  SalesLeaderboardData,
  AreaLeaderboardData,
} from '../../../types/leaderboard';
import { MONTH_NAMES } from '../utils';

import PodiumShowcase from './components/PodiumShowcase';
import SalesLeaderboardTable from './components/SalesLeaderboardTable';
import AreaLeaderboardTable from './components/AreaLeaderboardTable';

const { Title, Text } = Typography;

export const LeaderboardPage: React.FC = () => {
  const { message } = App.useApp();

  // Active Tab: 'sales' | 'area'
  const [activeTab, setActiveTab] = useState<'sales' | 'area'>('sales');

  // Filters State
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  // -1 = Live Current Month (omitted from param), 0 = Full Year Accumulative, 1..12 = Specific Month
  const [selectedMonth, setSelectedMonth] = useState<number>(-1);
  const [selectedAreaId, setSelectedAreaId] = useState<string>(''); // For sales tab internal area filter
  const [propertyAreas, setPropertyAreas] = useState<any[]>([]);
  const [loadingAreasFilter, setLoadingAreasFilter] = useState<boolean>(true);

  // Sales Leaderboard Data State
  const [salesData, setSalesData] = useState<SalesLeaderboardData | null>(null);
  const [loadingSales, setLoadingSales] = useState<boolean>(true);
  const [salesPage, setSalesPage] = useState<number>(1);
  const [salesLimit, setSalesLimit] = useState<number>(10);
  const [salesSearch, setSalesSearch] = useState<string>('');
  const [salesSortBy, setSalesSortBy] = useState<'omzet' | 'ok' | 'rsv'>('omzet');
  const [salesSortOrder, setSalesSortOrder] = useState<'desc' | 'asc'>('desc');

  // Area Leaderboard Data State
  const [areaData, setAreaData] = useState<AreaLeaderboardData | null>(null);
  const [loadingArea, setLoadingArea] = useState<boolean>(true);
  const [areaSortBy, setAreaSortBy] = useState<'omzet' | 'ok' | 'rsv' | 'name'>('omzet');
  const [areaSortOrder, setAreaSortOrder] = useState<'desc' | 'asc'>('desc');

  // Fetch Property Areas for Role
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        setLoadingAreasFilter(true);
        const res = await getPropertyAreasForRole();
        if (res?.status === 'success') {
          setPropertyAreas(res.data || []);
          if (res.data?.length === 1) {
            setSelectedAreaId(res.data[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load property areas filter:', err);
      } finally {
        setLoadingAreasFilter(false);
      }
    };
    fetchAreas();
  }, []);

  // Fetch Sales Leaderboard
  const fetchSales = useCallback(async () => {
    try {
      setLoadingSales(true);
      const params: any = {
        periode: selectedYear,
        page: salesPage,
        limit: salesLimit,
        sort_by: salesSortBy,
        sort_order: salesSortOrder,
      };

      // Month logic: -1 omitted (Live Current Month), 0 = Full Year, 1..12 = Specific Month
      if (selectedMonth !== -1) {
        params.month = selectedMonth;
      }

      if (selectedAreaId) {
        params.property_area_id = selectedAreaId;
      }

      if (salesSearch) {
        params.search = salesSearch;
      }

      const res = await getSalesLeaderboard(params);
      if (res?.status === 'success') {
        setSalesData(res.data);
      } else {
        message.error(res?.message || 'Gagal memuat leaderboard sales.');
      }
    } catch (err) {
      console.error('Failed to fetch sales leaderboard:', err);
      message.error('Gagal memuat leaderboard sales.');
    } finally {
      setLoadingSales(false);
    }
  }, [
    selectedYear,
    selectedMonth,
    selectedAreaId,
    salesPage,
    salesLimit,
    salesSortBy,
    salesSortOrder,
    salesSearch,
    message,
  ]);

  // Fetch Area Leaderboard
  const fetchArea = useCallback(async () => {
    try {
      setLoadingArea(true);
      const params: any = {
        periode: selectedYear,
        sort_by: areaSortBy,
        sort_order: areaSortOrder,
      };

      if (selectedMonth !== -1) {
        params.month = selectedMonth;
      }

      const res = await getAreaLeaderboard(params);
      if (res?.status === 'success') {
        setAreaData(res.data);
      } else {
        message.error(res?.message || 'Gagal memuat leaderboard kawasan.');
      }
    } catch (err) {
      console.error('Failed to fetch area leaderboard:', err);
      message.error('Gagal memuat leaderboard kawasan.');
    } finally {
      setLoadingArea(false);
    }
  }, [selectedYear, selectedMonth, areaSortBy, areaSortOrder, message]);

  // Trigger Data Fetch based on active tab & dependencies
  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSales();
    } else {
      fetchArea();
    }
  }, [activeTab, fetchSales, fetchArea]);

  // Handler for Sales Table Change (sorting & pagination)
  const handleSalesTableChange = (pagination: any, filters: any, sorter: any) => {
    if (pagination) {
      setSalesPage(pagination.current || 1);
      setSalesLimit(pagination.pageSize || 10);
    }

    if (sorter && sorter.order) {
      const activeKey = sorter.columnKey || sorter.column?.key;
      if (activeKey === 'omzet' || activeKey === 'ok' || activeKey === 'rsv') {
        setSalesSortBy(activeKey);
        setSalesSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
      }
    } else if (sorter && !sorter.order) {
      setSalesSortBy('omzet');
      setSalesSortOrder('desc');
    }
  };

  // Handler for Area Table Change (sorting)
  const handleAreaTableChange = (pagination: any, filters: any, sorter: any) => {
    if (sorter && sorter.order) {
      const activeKey = sorter.columnKey || sorter.column?.key;
      if (activeKey === 'property_area_name' || activeKey === 'name') {
        setAreaSortBy('name');
      } else if (activeKey === 'omzet' || activeKey === 'ok' || activeKey === 'rsv') {
        setAreaSortBy(activeKey);
      }
      setAreaSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
    } else if (sorter && !sorter.order) {
      setAreaSortBy('omzet');
      setAreaSortOrder('desc');
    }
  };

  // Handler for Sales Search
  const handleSalesSearch = (value: string) => {
    setSalesSearch(value);
    setSalesPage(1); // Reset to page 1 on new search
  };

  // Current Month name for live label
  const liveMonthName = MONTH_NAMES[dayjs().month()];

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100%',
        paddingBottom: '40px',
        overflow: 'hidden',
      }}
    >
      {/* Ambient Radial Glowing Orbs Aligned with PerformanceDashboard.tsx */}
      <div
        style={{
          position: 'absolute',
          top: '-100px',
          left: '-150px',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(123, 170, 24, 0.12) 0%, rgba(255, 255, 255, 0) 75%)',
          filter: 'blur(60px)',
          zIndex: 0,
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '30%',
          right: '-200px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(24, 144, 255, 0.08) 0%, rgba(255, 255, 255, 0) 75%)',
          filter: 'blur(80px)',
          zIndex: 0,
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-150px',
          left: '10%',
          width: '450px',
          height: '450px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(250, 173, 20, 0.06) 0%, rgba(255, 255, 255, 0) 75%)',
          filter: 'blur(70px)',
          zIndex: 0,
          pointerEvents: 'none',
          willChange: 'transform',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Title Header (Matching PerformanceDashboard.tsx exactly) */}
        <div
          style={{
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0, color: '#1e293b', fontWeight: 700 }}>
              Leaderboard Sales & Kawasan
            </Title>
            <Text type="secondary">
              Papan pemeringkatan performa real-time dan gamifikasi untuk memacu kompetisi sehat & apresiasi pencapaian terbaik.
            </Text>
          </div>

          {/* Global Time & Area Filters */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <Space>
              <FilterOutlined style={{ color: '#64748b' }} />
              <Text type="secondary">Filter:</Text>
            </Space>

            {/* Area Filter (Only visible on Sales tab) */}
            {activeTab === 'sales' && (
              <Select
                loading={loadingAreasFilter}
                value={selectedAreaId}
                onChange={(val) => {
                  setSelectedAreaId(val);
                  setSalesPage(1);
                }}
                style={{ width: 180 }}
                placeholder="Semua Kawasan"
              >
                {propertyAreas.length > 1 && (
                  <Select.Option value="">Semua Kawasan</Select.Option>
                )}
                {propertyAreas.map((area) => (
                  <Select.Option key={area.id} value={area.id}>
                    {area.name}
                  </Select.Option>
                ))}
              </Select>
            )}

            {/* Month Filter */}
            <Select
              value={selectedMonth}
              onChange={(val) => {
                setSelectedMonth(val);
                setSalesPage(1);
              }}
              style={{ width: 170 }}
            >
              <Select.Option value={-1}>Bulan Berjalan (Live)</Select.Option>
              <Select.Option value={0}>Setahun Penuh</Select.Option>
              {MONTH_NAMES.map((name, idx) => (
                <Select.Option key={idx + 1} value={idx + 1}>
                  {name}
                </Select.Option>
              ))}
            </Select>

            {/* Year Filter */}
            <Select
              value={selectedYear}
              onChange={(val) => {
                setSelectedYear(val);
                setSalesPage(1);
              }}
              style={{ width: 100 }}
            >
              {[2024, 2025, 2026, 2027].map((year) => (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        {/* Tab Switcher & Active Time Context */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          <Segmented
            value={activeTab}
            onChange={(val) => setActiveTab(val as 'sales' | 'area')}
            options={[
              { label: 'Leaderboard Sales Agent', value: 'sales' },
              { label: 'Leaderboard Kawasan Properti', value: 'area' },
            ]}
          />

          <Text type="secondary" style={{ fontSize: '13px' }}>
            <CalendarOutlined style={{ marginRight: '6px', color: '#64748b' }} />
            {selectedMonth === -1
              ? `Bulan Berjalan: ${liveMonthName} ${selectedYear} (Live)`
              : selectedMonth === 0
              ? `Akumulasi 1 Tahun Penuh ${selectedYear}`
              : `Bulan ${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`}
          </Text>
        </div>

        {/* Tab Content: Sales Leaderboard */}
        {activeTab === 'sales' && (
          <>
            {/* Section 1: Podium Top 3 Sales */}
            <PodiumShowcase
              type="sales"
              salesPodium={salesData?.podium}
              loading={loadingSales}
            />

            {/* Section 2: Complete Sales Ranking Table */}
            <SalesLeaderboardTable
              dataSource={salesData?.leaderboard || []}
              loading={loadingSales}
              page={salesPage}
              limit={salesLimit}
              total={salesData?.pagination?.total || 0}
              sortBy={salesSortBy}
              sortOrder={salesSortOrder}
              onTableChange={handleSalesTableChange}
              onSearch={handleSalesSearch}
            />
          </>
        )}

        {/* Tab Content: Area Leaderboard */}
        {activeTab === 'area' && (
          <>
            {/* Section 1: Podium Top 3 Kawasan */}
            <PodiumShowcase
              type="area"
              areaPodium={areaData?.podium}
              loading={loadingArea}
            />

            {/* Section 2: Complete Area Ranking Table */}
            <AreaLeaderboardTable
              dataSource={areaData?.leaderboard || []}
              loading={loadingArea}
              sortBy={areaSortBy}
              sortOrder={areaSortOrder}
              onTableChange={handleAreaTableChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
